import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { isAuthenticated, addTeacher } from "../../../ApiService/ApiService";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchStatesAsync,
  selectAllStates,
  selectStatesLoading,
  selectStatesError,
} from "../../../Slices/statesSlice";
import {
  isValidEmail,
  isValidPin,
  isValidPhoneNumber,
} from "../../../Utils/validFormDetails";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddTeacher({ fetchTeachers, setShowAddTeacher }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStatesAsync());
  }, [dispatch]);
  const states = useSelector(selectAllStates);
  const statesLoading = useSelector(selectStatesLoading);
  const statesError = useSelector(selectStatesError);
  const schoolId = parseInt(sessionStorage.getItem("schoolId"));
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    schoolId: schoolId,
    street1: "",
    street2: "",
    city: "",
    stateId: "",
    pin: "",
    phone: "",
    gender: "",
    doj: "",
  });

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, doj: date });
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.city ||
      !formData.stateId ||
      !formData.pin ||
      !formData.phone ||
      !formData.gender ||
      !formData.doj
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill in all the required details.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidEmail(formData.email)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid email address.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    if (!isValidPin(formData.pin)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 6-digit PIN code.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (!isValidPhoneNumber(formData.phone)) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please enter a valid 10-digit phone number.",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Passwords didn't match",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }

    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addTeacher(formData);
        // console.log("RES", response);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "Teacher added",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setFormData({
              firstName: "",
              middleName: "",
              lastName: "",
              email: "",
              password: "",
              confirmPassword: "",
              schoolId: schoolId,
              street1: "",
              street2: "",
              city: "",
              stateId: "",
              pin: "",
              phone: "",
              gender: "",
              doj: "",
            });
            fetchTeachers();
            setShowAddTeacher(false);
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add Teacher
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  size="small"
                  required
                >
                  <MenuItem value="1">Male</MenuItem>
                  <MenuItem value="2">Female</MenuItem>
                  <MenuItem value="3">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Street1"
                  name="street1"
                  value={formData.street1}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Street2"
                  name="street2"
                  value={formData.street2}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="state">State</InputLabel>
                  <Select
                    name="stateId"
                    label="State"
                    labelId="state"
                    value={formData.stateId}
                    onChange={handleChange}
                    size="small"
                    disabled={statesLoading}
                  >
                    {statesLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : statesError ? (
                      <MenuItem value="">Error fetching states</MenuItem>
                    ) : (
                      states.map((state) => (
                        <MenuItem key={state?.id} value={state?.id}>
                          {state?.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pin"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Typography sx={{ fontSize: 14, mb: 1, ml: 1 }}>
                  Date of Joining *
                </Typography>
                <TextField
                  fullWidth
                  name="doj"
                  type="date"
                  labelid="doj"
                  value={formData.doj}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddTeacher(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                {" "}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddTeacher;
